import { useEffect, useState } from "react";
import CountUp from "react-countup";
import { motion } from "framer-motion";

type ImpactItem = {
  id: string; // Database ID connect karne ke liye
  label: string;
  hindiLabel: string;
  value: number;
  suffix: string;
  icon: string;
  description: string;
};

const defaultImpactData: ImpactItem[] = [
  {
    id: "lives_impacted",
    label: "Lives Impacted",
    hindiLabel: "लोगों तक सहायता",
    value: 250000,
    suffix: "+",
    icon: "👥",
    description:
      "Sevaa Foundation has supported communities through education, healthcare, hunger support and social welfare initiatives.",
  },
  {
    id: "villages_reached",
    label: "Villages Reached",
    hindiLabel: "गांवों तक पहुंच",
    value: 1200,
    suffix: "+",
    icon: "🏡",
    description:
      "Our NGO work reaches villages and communities with social support, awareness and welfare services.",
  },
  {
    id: "active_volunteers",
    label: "Active Volunteers",
    hindiLabel: "सक्रिय स्वयंसेवक",
    value: 5400,
    suffix: "+",
    icon: "❤️",
    description:
      "Volunteers support Sevaa Foundation in donation drives, education support, healthcare camps and community service.",
  },
  {
    id: "states_covered",
    label: "States Covered",
    hindiLabel: "राज्यों में सेवा",
    value: 18,
    suffix: "+",
    icon: "🗺️",
    description:
      "Sevaa Foundation works across India to support people through charity, NGO campaigns and social welfare programs.",
  },
];

const Impact = () => {
  const [dynamicImpact, setDynamicImpact] = useState<ImpactItem[]>(defaultImpactData);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // const res = await fetch(`http://localhost/seva-backend/api/stats/get_stats.php?t=${new Date().getTime()}`);
        const res = await fetch(`https://www.sevaafoundation.com/seva-backend/api/stats/get_stats.php?t=${new Date().getTime()}`);
        const data = await res.json();
        
        if (data.success && data.stats) {
          setDynamicImpact(prev => 
            prev.map(item => ({
              ...item,
              // String count ko wapas Number me convert kar rahe hain CountUp animation ke liye
              value: data.stats[item.id] ? parseInt(data.stats[item.id].toString().replace(/,/g, '')) : item.value 
            }))
          );
        }
      } catch (error) {
        console.error("Failed to load impact stats", error);
      }
    };
    fetchStats();
  }, []);

  return (
    <section
      id="impact"
      aria-labelledby="impact-heading"
      className="relative py-12 bg-[#0f766e] overflow-hidden font-poppins"
    >
      {/* Subtle Background Accent */}
      <div
        className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none"
        aria-hidden="true"
      ></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="inline-block bg-white/10 text-[#ffb000] text-xs font-black uppercase tracking-[0.25em] px-4 py-2 rounded-full mb-4">
            Sevaa Foundation Impact
          </span>

          <h2
            id="impact-heading"
            className="text-4xl md:text-5xl font-black text-white leading-tight"
          >
            Our National Impact
          </h2>

          <p className="text-[#a7f3d0] font-medium mt-4 text-lg max-w-3xl mx-auto">
            Real action delivering real change across India through child
            education, healthcare support, hunger relief, women empowerment and
            social welfare.
          </p>

          {/* SEO + Accessibility supportive Hindi-English text */}
          <p className="sr-only">
            Sevaa Foundation ek registered NGO hai jo Bharat me bachchon ki
            padhai, healthcare camp, mahila sashaktikaran, bhojan sahayata,
            charity donation aur samaj seva ke liye kaam karta hai.
          </p>
        </div>

        {/* Centered Impact Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-8">
          {dynamicImpact.map((item, index) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="flex flex-col items-center justify-center text-center group"
              aria-label={`${item.label}: ${item.value.toLocaleString(
                "en-IN"
              )}${item.suffix}`}
            >
              {/* Centered Icon with Hover Effect */}
              <div
                className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center text-4xl mb-6 group-hover:bg-[#ffb000] group-hover:scale-110 group-hover:text-black transition-all duration-300 shadow-lg"
                aria-hidden="true"
              >
                {item.icon}
              </div>

              {/* Huge Centered Number */}
              <h3 className="text-4xl md:text-5xl font-black text-white mb-2">
                <span className="sr-only">
                  {item.value.toLocaleString("en-IN")}
                  {item.suffix} {item.label}
                </span>

                <span aria-hidden="true">
                  <CountUp
                    end={item.value}
                    duration={3}
                    separator=","
                    enableScrollSpy
                    scrollSpyOnce
                  />
                  <span className="text-[#ffb000] ml-1">{item.suffix}</span>
                </span>
              </h3>

              {/* Clean Centered Text */}
              <p className="text-white/90 font-bold text-sm md:text-base">
                {item.label}
              </p>

              <p className="text-[#a7f3d0]/90 font-medium text-xs md:text-sm mt-1">
                {item.hindiLabel}
              </p>

              <p className="sr-only">{item.description}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Impact;